import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type IDriveFolderSelectorModalProps from "./type";
import type {
    DriveFolderPayloadType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    FolderOutputIcon,
    ArrowLeftIcon,
    FolderIcon
} from "lucide-react-native";
import RESTServiceContext from "../../services/restAPI/context";

const DriveFolderSelectorModal = ({
    isVisible,
    onClose,
    onMove
}: IDriveFolderSelectorModalProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        currentFolderID,
        setCurrentFolderID
    ] = useState<string | undefined>(undefined);

    const [
        folderHistory,
        setFolderHistory
    ] = useState<{ id: string | undefined; name: string }[]>([
        {
            name: "Ana Dizin",
            id: undefined
        }
    ]);

    const [
        folders,
        setFolders
    ] = useState<DriveFolderPayloadType[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchDriveItems = useCallback(async (folderID: string | undefined) => {
        if(!isVisible) return;

        try {
            setLoading(true);

            const folderParams: { parentFolderID?: string } = {};
            if(folderID) {
                folderParams.parentFolderID = folderID;
            }
            const foldersResult = await RESTServiceContext.action("getAllDriveFolders", folderParams);

            if(foldersResult.data) {
                setFolders(foldersResult.data as unknown as DriveFolderPayloadType[]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [isVisible]);

    useEffect(() => {
        if(isVisible) {
            fetchDriveItems(currentFolderID);
        } else {
            setCurrentFolderID(undefined);
            setFolderHistory([
                {
                    name: "Ana Dizin",
                    id: undefined
                }
            ]);
        }
    }, [
        currentFolderID,
        fetchDriveItems,
        isVisible
    ]);

    const handleNavigateFolder = (folderID: string, folderName: string) => {
        setCurrentFolderID(folderID);
        setFolderHistory((prev) => [
            ...prev,
            {
                name: folderName,
                id: folderID
            }
        ]);
    };

    const handleGoBack = () => {
        setFolderHistory((prev) => {
            const newHistory = [
                ...prev
            ];
            newHistory.pop();
            const previousFolder = newHistory[newHistory.length - 1];
            setCurrentFolderID(previousFolder ? previousFolder.id : undefined);
            return newHistory;
        });
    };

    const currentFolderName = folderHistory[folderHistory.length - 1]?.name || "Ana Dizin";

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerTitleContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    folderHistory.length > 1 ?
                        <Button
                            icon={() => <ArrowLeftIcon
                                color={colors.project.brandWhite}
                                size={24}
                            />}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="free"
                            onPress={handleGoBack}
                            variant="filled"
                        />
                    :
                        null
                }
                <Text
                    variant="titleLargeSize"
                >
                    {currentFolderName}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.headerActions,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    onPress={() => onMove(currentFolderID)}
                    icon={({
                        color,
                        size
                    }) => <FolderOutputIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                    title="Buraya Taşı"
                />
            </View>
        </View>;
    };

    const renderFolder = (folder: DriveFolderPayloadType, index: number) => {
        const cardWidth = isMobile ? "48%" : 200;
        return <View
            style={[
                stylesheet.itemWrapper,
                {
                    width: cardWidth as unknown as number
                }
            ]}
            key={folder._id || `folder-${index}`}
        >
            <TouchableOpacity
                onPress={() => handleNavigateFolder(folder._id, folder.name)}
                style={[
                    stylesheet.itemContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg,
                        gap: spaces.spacingSm
                    }
                ]}
                activeOpacity={0.7}
            >
                <FolderIcon
                    color={colors.content.icon.mid}
                    size={80}
                />
                <Text
                    style={[
                        stylesheet.itemTitle,
                        {
                            marginTop: spaces.spacingSm
                        }
                    ]}
                    variant="bodyMediumSize"
                    numberOfLines={1}
                >
                    {folder.name}
                </Text>
            </TouchableOpacity>
        </View>;
    };

    return <Dialog
        bottomComponent={
            <View
                style={[
                    stylesheet.modalBottomContainer,
                    {
                        paddingHorizontal: spaces.spacingLg,
                        paddingBottom: spaces.spacingLg
                    }
                ]}
            >
                <Button
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="stretch"
                    onPress={onClose}
                    variant="filled"
                    title="Kapat"
                />
            </View>
        }
        contentContainerStyle={[
            stylesheet.dialogContentContainer,
            {
                minWidth: isMobile ? "90%" : 800
            }
        ]}
        onOverlayPress={onClose}
        isVisible={isVisible}
        onClosed={onClose}
    >
        <View
            style={stylesheet.contentContainer}
        >
            {renderHeader()}
            {
                loading ?
                    <View
                        style={stylesheet.emptyContainer}
                    >
                        <ActivityIndicator
                            color={colors.content.icon.mid}
                            size="large"
                        />
                    </View>
                :
                    folders.length === 0 ?
                        <View
                            style={stylesheet.emptyContainer}
                        >
                            <FolderIcon
                                color={colors.content.icon.mid}
                                size={48}
                            />
                            <Text
                                style={[
                                    stylesheet.emptyText,
                                    {
                                        marginTop: spaces.spacingSm
                                    }
                                ]}
                                variant="bodyMediumSize"
                            >
                                { "Bu dizinde klasör bulunmuyor."}
                            </Text>
                        </View>
                :
                        <ScrollView
                            showsVerticalScrollIndicator={false}
                            style={{
                                flex: 1
                            }}
                        >
                            <View
                                style={[
                                    stylesheet.gridContainer,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                {folders.map(renderFolder)}
                            </View>
                        </ScrollView>
            }
        </View>
    </Dialog>;
};

export default DriveFolderSelectorModal;
